import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Clock, MapPin, Sparkles } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { activityService } from '@/lib/api/activity.service';
import { eventService } from '@/lib/api/event.service';
import { formatShortDate } from '@/utils/dateFormat';
import type { CreateActivityDto, ActivityStatus } from '@/types/activity.types';

export const ActivityForm: React.FC = () => {
  const { eventId, id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['activities', 'common']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [eventTitle, setEventTitle] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>('');
  const [eventStartTime, setEventStartTime] = useState<string>('');
  const [eventEndTime, setEventEndTime] = useState<string>('');
  const [formData, setFormData] = useState<Omit<CreateActivityDto, 'event_id'>>({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    status: 'draft' as ActivityStatus,
    leads: [],
    key_participants: [],
    vendors: []
  });

  const eventIdNum = parseInt(eventId || '0');
  const activityId = id ? parseInt(id) : null;
  const isEditMode = !!activityId;

  useEffect(() => {
    const loadData = async () => {
      try {
        const event = await eventService.getEvent(eventIdNum);
        setEventTitle(event.title);
        setEventDate(event.event_date);
        
        // Set event time boundaries
        if (event.start_time && event.end_time) {
          const eventStart = new Date(event.start_time).toISOString().slice(0, 16);
          const eventEnd = new Date(event.end_time).toISOString().slice(0, 16);
          setEventStartTime(eventStart);
          setEventEndTime(eventEnd);
          
          // For new activities, default start and end time to event start time
          if (!isEditMode) {
            setFormData(prev => ({
              ...prev,
              start_time: eventStart,
              end_time: eventStart
            }));
          }
        }

        if (isEditMode && activityId) {
          const activity = await activityService.getActivity(activityId);
          setFormData({
            title: activity.title,
            description: activity.description || '',
            start_time: activity.start_time ? new Date(activity.start_time).toISOString().slice(0, 16) : '',
            end_time: activity.end_time ? new Date(activity.end_time).toISOString().slice(0, 16) : '',
            location: activity.location || '',
            status: activity.status,
            leads: activity.leads?.map((l: any) => l.id || l.guest_id) || [],
            key_participants: activity.key_participants?.map((p: any) => p.guest_id) || [],
            vendors: activity.vendors?.map((v: any) => v.id || v.vendor_id) || []
          });
        }
      } catch (err) {
        setError(t('activities:form.errors.failedLoad'));
      }
    };

    if (eventIdNum) loadData();
  }, [eventIdNum, isEditMode, activityId]);

  const validateTimeConstraints = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (formData.start_time && formData.end_time && eventStartTime && eventEndTime) {
      const activityStart = new Date(formData.start_time);
      const activityEnd = new Date(formData.end_time);
      const eventStart = new Date(eventStartTime);
      const eventEnd = new Date(eventEndTime);
      
      if (activityStart < eventStart) {
        errors.start_time = `Activity start time must be on or after event start time (${new Date(eventStartTime).toLocaleString()})`;
      }
      
      if (activityEnd > eventEnd) {
        errors.end_time = `Activity end time must be on or before event end time (${new Date(eventEndTime).toLocaleString()})`;
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title) {
      setError(t('activities:form.validation.titleRequired'));
      return;
    }
    
    if (!validateTimeConstraints()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      if (isEditMode && activityId) {
        await activityService.updateActivity(activityId, formData);
      } else {
        await activityService.createActivity(eventIdNum, formData);
      }
      navigate(`/events/${eventIdNum}`);
    } catch (err: any) {
      // Handle backend validation errors
      if (err?.response?.data?.details?.validationErrors) {
        const backendErrors: Record<string, string> = {};
        err.response.data.details.validationErrors.forEach((error: any) => {
          backendErrors[error.field] = error.message;
        });
        setFieldErrors(backendErrors);
        setError(t('activities:form.validation.fixErrors'));
      } else {
        setError(err.response?.data?.message || (isEditMode ? t('activities:form.errors.failedUpdate') : t('activities:form.errors.failedCreate')));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'start_time' && value) {
        updated.end_time = value;
      }
      return updated;
    });
    // Clear field-specific errors when user makes changes
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const getMinEndTime = (): string => {
    if (!formData.start_time) return eventStartTime;
    return formData.start_time;
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('common:status.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <PageContainer width="default" className="space-y-4">
      <AppBreadcrumbs />
      {/* Compact Header */}
      <div className="flex items-center gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            {isEditMode ? t('activities:edit') : t('activities:new')}
          </h1>
          {eventTitle && (
            <p className="text-xs text-muted-foreground">
              {t('activities:form.forEvent', { title: eventTitle })}{eventDate && ` · ${formatShortDate(eventDate)}`}
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white rounded-lg border p-4 space-y-4">
          {/* Title */}
          <div>
            <Label htmlFor="title" className="text-xs font-medium">{t('activities:form.labels.activityTitle')}</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder={t('activities:form.placeholders.activityTitle')}
              required
              className="mt-1"
            />
          </div>

          {/* Event Time Range Info */}
          {eventStartTime && eventEndTime && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-900 mb-1">{t('activities:form.eventTimeRange.title')}</p>
              <p className="text-xs text-blue-700">
                {new Date(eventStartTime).toLocaleString()} - {new Date(eventEndTime).toLocaleString()}
              </p>
              <p className="text-xs text-blue-600 mt-1">{t('activities:form.eventTimeRange.mustFallWithin')}</p>
            </div>
          )}

          {/* Time - Start and End inline */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start_time" className="text-xs font-medium">{t('activities:form.labels.startTime')}</Label>
              <Input
                id="start_time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => handleInputChange('start_time', e.target.value)}
                min={eventStartTime}
                max={eventEndTime}
                className="mt-1"
                compact
              />
              {fieldErrors.start_time && (
                <span className="text-xs text-red-600 mt-1 block">{fieldErrors.start_time}</span>
              )}
            </div>
            <div>
              <Label htmlFor="end_time" className="text-xs font-medium">{t('activities:form.labels.endTime')}</Label>
              <Input
                id="end_time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => handleInputChange('end_time', e.target.value)}
                min={getMinEndTime()}
                max={eventEndTime}
                className="mt-1"
                compact
              />
              {fieldErrors.end_time && (
                <span className="text-xs text-red-600 mt-1 block">{fieldErrors.end_time}</span>
              )}
            </div>
          </div>

          {/* Location and Status - inline */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="location" className="text-xs font-medium flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {t('activities:form.labels.location')}
              </Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder={t('activities:form.placeholders.location')}
                className="mt-1"
                compact
              />
            </div>
            <div>
              <Label htmlFor="status" className="text-xs font-medium">{t('activities:form.labels.status')}</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value as ActivityStatus)}
                className="mt-1 w-full h-7 px-2 text-sm rounded-md border border-input bg-background"
              >
                <option value="draft">{t('activities:form.statusOptions.draft')}</option>
                <option value="planned">{t('activities:form.statusOptions.planned')}</option>
                <option value="published">{t('activities:form.statusOptions.published')}</option>
              </select>
            </div>
          </div>

          {/* Description - compact */}
          <div>
            <Label htmlFor="description" className="text-xs font-medium">{t('activities:fields.description')}</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('activities:form.placeholders.description')}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring resize-none"
              rows={2}
            />
          </div>

          {/* Status hint */}
          <p className="text-[10px] text-muted-foreground">
            {formData.status === 'draft' && t('activities:form.statusHints.draft')}
            {formData.status === 'planned' && t('activities:form.statusHints.planned')}
            {formData.status === 'published' && t('activities:form.statusHints.published')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/events/${eventIdNum}`)} disabled={loading}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={loading}>
            {loading && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />}
            <Save className="h-3 w-3 mr-1" />
            {isEditMode ? t('common:actions.save') : t('common:actions.create')}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
};
